import React, { useState } from "react";
import { 
  Box, 
  IconButton, 
  Paper, 
  TextField, 
  Tooltip, 
  Checkbox, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Chip, 
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Snackbar,
  Alert
} from "@mui/material";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import { useForm } from "react-hook-form";
import TitleIcon from "@mui/icons-material/Title";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import PeopleIcon from '@mui/icons-material/People';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDispatch, useSelector } from "react-redux";
import useKeyboardShortcut from "use-keyboard-shortcut";
import {
  resetIndex,
  selectedPageCardIndex,
  selectedTextCardIndex,
} from "../features/panels/panelsSlice";

const TextCard = ({
  id,
  text,
  editable = true,
  onEdit,
  onSubmitClick,
  handleDelete,
  isFocused = false,
  textIndex,
  pageIndex,
  isSelected = false,
  onSelect,
  fromExtraction = false,
  speaker = "",
  translationContext = {},
  onSpeakerEdit = null,
  onContextEdit = null,
  characterData = null,
}) => {
  const { register, handleSubmit } = useForm({});
  const [isEditingSpeaker, setIsEditingSpeaker] = useState(false);
  const [speakerValue, setSpeakerValue] = useState(speaker);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  
  const [contextDialogOpen, setContextDialogOpen] = useState(false);
  const [contextValues, setContextValues] = useState({
    emotional_state: translationContext?.emotional_state || "",
    speaker_relationship: translationContext?.speaker_relationship || ""
  });

  const selectedTextIndex = useSelector(selectedTextCardIndex);
  const selectedPageIndex = useSelector(selectedPageCardIndex);
  const dispatch = useDispatch();

  const hasTranslationContext = translationContext && 
    (translationContext.emotional_state || translationContext.speaker_relationship);

  const getCharacterOptions = () => {
    if (!characterData || !characterData.characters) return [];
    return characterData.characters.map(char => char.name);
  };

  const characterNames = getCharacterOptions();
  
  const { flushHeldKeys } = useKeyboardShortcut(
    ["Escape"],
    (shortcutKeys) => {
      if (textIndex != null && pageIndex != null && editable) {
        onHandleCross();
      }
      if (isEditingSpeaker) {
        setIsEditingSpeaker(false);
        setSpeakerValue(speaker);
      }
    },
    {
      overrideSystem: false,
      ignoreInputFields: false,
      repeatOnHold: false,
    }
  );

  const onHandleCross = () => {
    dispatch(resetIndex());
    flushHeldKeys();
  };

  const onEditBtnClick = () => {
    onEdit();
  };

  const onSubmit = (data) => {
    const t = data.updatedText;
    onSubmitClick(t);
    dispatch(resetIndex());
  };

  const handleEditSpeaker = () => {
    setIsEditingSpeaker(true);
  };

  const handleSaveSpeaker = () => {
    if (onSpeakerEdit && speakerValue !== speaker && speakerValue.trim() !== "") {
      onSpeakerEdit(speakerValue);
    }
    setIsEditingSpeaker(false);
  };

  const handleSelectChange = (e) => {
    setSpeakerValue(e.target.value);
  };

  const handleCloseContextDialog = () => {
    setContextDialogOpen(false);
  };
  
  const handleContextChange = (e) => {
    const { name, value } = e.target;
    setContextValues(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSaveContext = () => {
    if (onContextEdit) {
      onContextEdit(contextValues);
    }
    setContextDialogOpen(false);
  };

  const handleEditContext = () => {
    setContextDialogOpen(true);
  };
  
  const handleCopyText = () => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopySnackbarOpen(true);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };

  const handleCloseCopySnackbar = () => {
    setCopySnackbarOpen(false);
  };
  
  return (
    <Paper
      elevation={1}
      sx={{
        padding: "0 0.2rem",
        ":hover": {
          border: "1px solid grey",
          cursor: "grab",
        },
        ...(isFocused && {
          border: "1px solid grey",
        }),
      }}
    >
      <CardContent
        onDoubleClick={() =>
          editable &&
          selectedTextIndex === null &&
          selectedPageIndex === null &&
          onEditBtnClick()
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center">
            {onSelect && (
              <>
                <span>{textIndex + 1}</span>
                <Checkbox checked={isSelected} onChange={onSelect} />
                <TitleIcon />
              </>
            )}
            {!isEditingSpeaker ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {speaker ? (
                  <Chip 
                    icon={<PersonIcon />} 
                    label={speaker} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ ml: 1 }}
                    onDelete={editable && characterNames.length > 0 ? handleEditSpeaker : undefined}
                    deleteIcon={editable && characterNames.length > 0 ? <EditIcon /> : undefined}
                  />
                ) : null}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexWrap: 'wrap', gap: 1 }}>
                <FormControl sx={{ minWidth: 150 }} size="small">
                  <Select
                    value={speakerValue}
                    onChange={handleSelectChange}
                    displayEmpty
                    autoFocus
                  >
                    <MenuItem value="">
                      <em>Narrator</em>
                    </MenuItem>
                    {characterNames.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <IconButton size="small" color="primary" onClick={handleSaveSpeaker}>
                  <CheckIcon />
                </IconButton>
                
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={() => {
                    setIsEditingSpeaker(false);
                    setSpeakerValue(speaker);
                  }}
                >
                  <CloseOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </Box>
          {editable && fromExtraction &&(
            <CardActions>
              {textIndex !== selectedTextIndex ? (
                <Box>
                  <Tooltip title="Delete">
                    <IconButton color="primary" onClick={handleDelete}>
                      <DeleteForeverIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      color="primary"
                      onClick={onEditBtnClick}
                      disabled={
                        selectedTextIndex !== null && selectedPageIndex !== null
                      }
                    >
                      <EditOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Tooltip title="Delete">
                  <IconButton color="primary" onClick={handleDelete}>
                    <DeleteForeverIcon />
                  </IconButton>
                </Tooltip>
              )}
            </CardActions>
          )}
        </div>
        {textIndex !== selectedTextIndex ? (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography>{text}</Typography>
              <Tooltip title="Copy text">
                <IconButton 
                  size="small" 
                  onClick={handleCopyText}
                  sx={{ ml: 1, mt: -1 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {fromExtraction && (translationContext || speaker) && (
              <Accordion sx={{ mt: 1, backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography variant="caption" color="text.secondary">More Info</Typography>
                  {editable && (
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditContext();
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  {translationContext?.emotional_state && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmojiEmotionsIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        <strong>Emotional State:</strong> {translationContext.emotional_state}
                      </Typography>
                    </Box>
                  )}
                  {translationContext?.speaker_relationship && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        <strong>Relationship:</strong> {translationContext.speaker_relationship}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} onReset={onHandleCross}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                autoFocus
                hiddenLabel
                id="textEditField"
                variant="standard"
                fullWidth
                defaultValue={text}
                {...register("updatedText")}
              />
              <div>
                <Tooltip title="Close">
                  <IconButton type="reset" color="error">
                    <CloseOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </div>
              <div>
                <Tooltip title="Submit">
                  <IconButton type="submit" color="success">
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </form>
        )}
      </CardContent>
      <Dialog open={contextDialogOpen} onClose={handleCloseContextDialog}>
        <DialogTitle>Edit Translation Context</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Emotional State"
                name="emotional_state"
                value={contextValues.emotional_state}
                onChange={handleContextChange}
                fullWidth
                margin="normal"
                helperText="Character's emotional state (e.g., angry, happy, sad)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Speaker Relationship"
                name="speaker_relationship"
                value={contextValues.speaker_relationship}
                onChange={handleContextChange}
                fullWidth
                margin="normal"
                helperText="Relationship to listener or scene (e.g., talking to friend, enemy)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContextDialog} color="error">
            Cancel
          </Button>
          <Button onClick={handleSaveContext} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={handleCloseCopySnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseCopySnackbar} severity="success" sx={{ width: '100%' }}>
          Text copied to clipboard!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default TextCard;
